import React, {
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { sendSearchAnalyticsEvent } from '@mb/lib/analytics';
import { getUserLang, isEuEnv } from '@mb/lib/i18n';
import { handleSystemErrorReport } from '@mb/lib/utils';
import MBSearchFormV2 from 'components/Forms/MBSearchFormV2/MBSearchFormV2';
import UploadImageModal from 'components/Modals/UploadImageModal';
import type { ImageFile } from 'components/Modals/UploadImageModal/UploadImageModal.types';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import * as Styled from './SearchFormContainer.styles';
import { searchActionsEnum } from '../../../../pages/ProductSearch/config/constants';
import {
  fetchSuggestionsByQuery,
  initAutocomplete,
} from '../../api/autocomplete';
import { base64File, uploadSearchImage } from '../../api/imageSearch';
import {
  shouldRedirectToSearch,
  shouldRequestApi,
  isMainTabsBrowserHistory,
  shouldTriggerSearch,
  getEmail,
  convertFileToUploadFormat,
} from '../../helpers';
import { definedTranslation } from '../../i18n';
import {
  getIsOpenSelector,
  getQuerySelector,
  getShadowQuerySelector,
  isSuggestionsRequestedSelector,
  currentProductIndexSelector,
  getSuggestedItemsSelector,
  currentItemIndexSelector,
  xRequestIdSelector,
} from '../../redux/selectors/autocompleteSelector';
import {
  setQuery,
  setIsOpen,
  setIsSuggestionsRequested,
  setShadowQuery,
  setCurrentItemIndex,
  setHoveredItem,
  setCurrentProductIndex,
} from '../../redux/sliceCreators/autocompleteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ArrowEvents } from '../../types';

const isInspiration = window.location.pathname.includes('/inspiration');

interface SearchFormContainerProps {
  id?: string;
  styledForm?: React.ReactElement<React.FormHTMLAttributes<HTMLFormElement>>;
  onFocus?: (query: string) => void;
  onSubmit?: (query?: string) => void;
  onReset?: () => void;
  submitOnReset?: boolean;
}

const SearchFormContainer: React.FC<SearchFormContainerProps> = ({
  id,
  styledForm,
  onFocus,
  onSubmit,
  onReset,
  submitOnReset = false,
}) => {
  const [isImageSearchOpened, setIsImageSearchOpened] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const query = useAppSelector(getQuerySelector);
  const shadowQuery = useAppSelector(getShadowQuerySelector);
  const isSuggestionsRequested = useAppSelector(isSuggestionsRequestedSelector);
  const currentProductIndex = useAppSelector(currentProductIndexSelector);
  const currentItemIndex = useAppSelector(currentItemIndexSelector);
  const suggestedItems = useAppSelector(getSuggestedItemsSelector);
  const isOpen = useAppSelector(getIsOpenSelector);
  const xRequestId = useAppSelector(xRequestIdSelector);
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isLoggedIn = Boolean(getEmail());

  const placeholders = {
    default: intl.formatMessage(definedTranslation.placeholders.default),
    inspiration: intl.formatMessage(
      definedTranslation.placeholders.inspiration,
    ),
  };
  const visualSearchModal = {
    title: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.title,
    ),
    subtitle: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.subtitle,
    ),
    dragTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.dropzoneText,
    ),
    uploadButtonLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.dropzoneButton,
    ),
    orTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.or,
    ),
    searchInputPlaceholder: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.placeholder,
    ),
    searchInputButton: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.button,
    ),
    uploadingTextLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.uploading,
    ),
    wrongFormatErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.wrongType,
    ),
    tooManyFilesErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.tooManyFiles,
    ),
    imageNotFoundErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.imageNotFound,
    ),
    someErrorLabel: intl.formatMessage(
      definedTranslation.visualSearch.uploadModal.errors.someError,
    ),
  };
  const tooltipText = intl.formatMessage(
    definedTranslation.visualSearch.searchTooltip,
  );
  const newLabel = intl.formatMessage(definedTranslation.newLabel);
  const PLACEHOLDER = isInspiration
    ? placeholders.inspiration
    : placeholders.default;

  useLayoutEffect(() => {
    const isMainTabsHistory = isMainTabsBrowserHistory();
    if (!isMainTabsHistory) {
      sessionStorage.removeItem('persistentQuery');
      sessionStorage.removeItem('persistentColor');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('searchInputSetFocus', handleSearchInputSetFocus);
    window.isSearchFormReady = true;
    window.dispatchEvent(new CustomEvent('search-form-ready'));

    return () => {
      window.removeEventListener(
        'searchInputSetFocus',
        handleSearchInputSetFocus,
      );
      window.isSearchFormReady = false;
    };
  }, [searchRef.current]);

  useEffect(() => {
    window.addEventListener('handleMiniPdpCloseEvent', handleMiniPdpClose);
    return () => {
      window.removeEventListener('handleMiniPdpCloseEvent', handleMiniPdpClose);
    };
  }, [shadowQuery, currentProductIndex]);

  useEffect(() => {
    triggerInitSearch();
    document.addEventListener('searchQueryResultsChange', handleQueryChange);
    return () => {
      document.removeEventListener(
        'searchQueryResultsChange',
        handleQueryChange,
      );
    };
  }, []);

  const handleMiniPdpClose = () => {
    if (currentProductIndex) {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }
  };

  const handleArrows = (e: React.KeyboardEvent) => {
    switch (e.keyCode) {
      case 38:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Up));
        break;
      case 40:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Down));
        break;
      case 37:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Left));
        break;
      case 39:
        window.dispatchEvent(new CustomEvent(ArrowEvents.Right));
        break;
      default:
        break;
    }
  };

  const triggerInitSearch = () => {
    shouldTriggerSearch(query) && handleSearch({ query });
  };

  const handleSearchInputSetFocus = () => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  };

  const handleQueryChange = (event: CustomEvent) => {
    const newQuery = event.detail?.query ?? '';
    dispatch(setQuery(newQuery));
    //sessionStorage.setItem('persistentQuery', newQuery);
    if (newQuery === '') {
      sessionStorage.removeItem('persistentColor');
    }
  };

  const handleInputFocus = (currentQuery: string) => {
    dispatch(setIsOpen(true));

    // Keep popup open with same state after miniPDP closed and put focus in Search form
    if (currentProductIndex != null) {
      return;
    }

    if (currentQuery?.length > 0) {
      handleInputChange(currentQuery);
    } else {
      onFocus?.(currentQuery);

      if (shouldRequestApi()) {
        dispatch(initAutocomplete(currentQuery));
      }
    }
  };

  const handleInputChange = (newQuery: string) => {
    dispatch(setShadowQuery(undefined));
    dispatch(setQuery(newQuery));
    dispatch(setCurrentItemIndex(undefined));
    dispatch(setCurrentProductIndex(undefined));
    dispatch(setHoveredItem(undefined));
    if (newQuery?.length < 3 && isSuggestionsRequested) {
      dispatch(setIsSuggestionsRequested(false));
    }
    if (shouldRequestApi()) {
      timerRef.current && clearTimeout(timerRef.current);
      const newTimer = setTimeout(() => {
        if (newQuery.length > 2) {
          dispatch(fetchSuggestionsByQuery(newQuery));
        } else {
          dispatch(initAutocomplete(newQuery));
        }
      }, 350);
      timerRef.current = newTimer;
    }
  };

  const handleSubmit = (args: any, isReset: boolean = false) => {
    window.splitFactory
      ?.client()
      ?.track('user', 'mb_frontend_homepage_search', undefined, {
        search_query: args,
        type: 'search_bar',
      });

    if (currentProductIndex != null && !isReset) {
      const product = suggestedItems?.[currentProductIndex];

      window.dispatchEvent(
        new CustomEvent('initMiniPDP', {
          detail: {
            id: product.variantId || product.id,
            clickedProductRequestId: xRequestId,
          },
        }),
      );
      dispatch(setCurrentProductIndex(undefined));
      return;
    }

    if (!isLoggedIn) {
      window.splitFactory
        ?.client()
        ?.track('user', 'mb_guest_search', undefined, {});
    }

    const shadowQ = isReset ? undefined : shadowQuery;
    onSubmit?.(shadowQ || args);
    sessionStorage.setItem('ssScroll', '0');
    sessionStorage.setItem('prevSearchUrl', 'null');
    //sessionStorage.setItem('persistentQuery', args);
    dispatch(setHoveredItem(undefined));
    dispatch(setIsOpen(false));
    window.scrollTo(0, 0);
    handleSearch({ query: shadowQ || args });
  };

  const handleFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    handleSubmit(shadowQuery || query);
  };

  const handleImageSearchFormSubmit = async (value: string) => {
    setUploadErrors([]);
    setIsUploadLoading(true);
    const data = await uploadSearchImage(value);
    setIsUploadLoading(false);
    if (data.item != null) {
      setIsImageSearchOpened(false);
      window.dispatchEvent(
        new CustomEvent('initVisualSearch', {
          detail: { item: data.item },
        }),
      );
    } else {
      setUploadErrors([
        data?.errorMessage || visualSearchModal.imageNotFoundErrorLabel,
      ]);
    }
  };

  const handleUploadFile = async (file: base64File) => {
    const data = await uploadSearchImage(null, file);
    setIsUploadLoading(false);
    if (data.item != null) {
      setIsImageSearchOpened(false);
      window.dispatchEvent(
        new CustomEvent('initVisualSearch', {
          detail: { item: data.item },
        }),
      );
    } else {
      setUploadErrors([data?.errorMessage || visualSearchModal.someErrorLabel]);
    }
  };

  const handleImageDrop = async (imageFiles: ImageFile[]) => {
    if (imageFiles.length === 0) {
      return;
    }
    setUploadErrors([]);
    setIsUploadLoading(true);
    const imageFile = await convertFileToUploadFormat(imageFiles?.[0]);
    handleUploadFile(imageFile);
  };

  const handleSearch = (args: any) => {
    if (shouldRedirectToSearch()) {
      const storageLang = getUserLang();
      const searchUrlPrefix = !isEuEnv
        ? ''
        : storageLang != null
        ? `/${storageLang}`
        : '';
      window.location.assign(
        `${searchUrlPrefix}/search?q=${encodeURIComponent(
          args.query || '',
        )}&searchType=Pure search`,
      );
    }

    const searchFormSubmit = new CustomEvent('searchFormSubmit', {
      detail: {
        query: args.query,
        action: searchActionsEnum.searchBar,
        searchType: 'Pure search',
        additionalFilters: {},
      },
    });
    document.dispatchEvent(searchFormSubmit);
    sendSearchAnalyticsEvent({
      query: args.query,
      searchType: 'pure search',
    });
  };

  const handleClose = () => {
    console.log('handleClose');
  };

  const handleReset = () => {
    onReset?.();
    handleInputChange('');
    setTimeout(() => {
      handleSearchInputSetFocus();
    }, 200);
    if (submitOnReset) {
      handleSubmit('', true);
    }
  };

  const handleImageSearchClick = () => {
    setIsImageSearchOpened(true);
  };
  const handleModalClose = () => {
    setUploadErrors([]);
    setIsImageSearchOpened(false);
  };

  const handleBackButtonClick = () => {
    dispatch(setIsOpen(false));
    window.dispatchEvent(
      new CustomEvent('toggle-main-search', { detail: { show: false } }),
    );
  };

  const handleClick = () => {
    if (!isOpen) {
      dispatch(setIsOpen(true));
    }
  };

  const handleDragOver = (e: React.SyntheticEvent) => {
    e.preventDefault();
  };

  const handleOnDrop = async (ev: any) => {
    try {
      ev.preventDefault();
      let file = undefined;
      if (ev.dataTransfer.items) {
        const itemFile = [...ev.dataTransfer.items]?.[0];
        if (itemFile.kind === 'file') {
          file = itemFile.getAsFile();
        }
      } else {
        file = [...ev.dataTransfer.files]?.[0];
      }
      if (file != null) {
        setIsImageSearchOpened(true);
        setUploadErrors([]);
        setIsUploadLoading(true);
        const base64 = await convertFileToUploadFormat(file);
        handleUploadFile(base64);
      }
    } catch (e) {
      handleSystemErrorReport(
        `Error in SearchBar handleOnDrop: ${e.message}`,
        'handleOnDrop',
      );
      console.log('handleOnDrop SearchBar', e);
    }
  };

  const FormElement = React.isValidElement(styledForm)
    ? () => styledForm
    : styledForm || Styled.Form;
  const withImageSearch = !isMobile && isLoggedIn;

  return (
    <>
      <Styled.Container
        onKeyDown={handleArrows}
        onDrop={handleOnDrop}
        onDragOver={handleDragOver}
      >
        {isOpen && (
          <Styled.BackButton iconName="back" onClick={handleBackButtonClick} />
        )}
        <FormElement
          data-selector="submit-search-form"
          id={id || 'main_search_form'}
          onSubmit={handleFormSubmit}
        >
          <MBSearchFormV2
            isVisualSearchNewFeature={false}
            newTooltipLabel={newLabel}
            imageSearchTooltip={tooltipText}
            ref={searchRef}
            withImageSearch={withImageSearch}
            onInputFocus={() => handleInputFocus(shadowQuery || query)}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onClick={handleClick}
            onClose={handleClose}
            onReset={handleReset}
            value={shadowQuery || query}
            placeholder={PLACEHOLDER}
            onImageSearchClick={handleImageSearchClick}
            isLeftRightArrowsActive={currentItemIndex == null}
          />
        </FormElement>
      </Styled.Container>
      <UploadImageModal
        showLinkForm
        showCloseIcon={!isUploadLoading}
        isLoading={isUploadLoading}
        showBottomActions={false}
        open={isImageSearchOpened}
        onClose={handleModalClose}
        onSearchFormSubmit={handleImageSearchFormSubmit}
        customErrorMessages={uploadErrors}
        onFilesSelected={handleImageDrop}
        {...visualSearchModal}
      />
    </>
  );
};

export default SearchFormContainer;
